// Order Management v1 — canonical shapes (cross-repo api-contract §4).
// The customer portal consumes the top-level /api/v1 order endpoints.

export type OrderStatus =
  | 'CREATED'
  | 'SUBMITTED_TO_BRANCH'
  | 'UNDER_REVIEW'
  | 'UPDATED'
  | 'DELIVERED'
  | 'CLOSED'
  | 'CANCELLED';

export type OrderItemStatus = 'REQUESTED' | 'AVAILABLE' | 'UNAVAILABLE' | 'SUBSTITUTED';

export interface OrderItem {
  id: string;
  name: string;
  quantity: number;
  unit?: string | null;
  status: OrderItemStatus;
  organizerNote?: string | null;
}

export interface OrderEvent {
  id: string;
  eventType: string;
  actorId: string;
  payload?: Record<string, unknown> | null;
  createdAt: string;
}

export interface Order {
  id: string;
  orderNumber: number;
  customerId: string;
  branchId: string;
  status: OrderStatus;
  note?: string | null;
  items: OrderItem[];
  events?: OrderEvent[];
  createdAt: string;
  updatedAt?: string;
  closedAt?: string | null;
}

// Compact order summary embedded on ORDER_REF chat messages.
export interface OrderSummary {
  id: string;
  orderNumber: number;
  status: OrderStatus;
  itemCount: number;
}

export type ChatMessageType = 'TEXT' | 'ORDER_REF' | 'COMMENT';
export type SenderRole = 'CUSTOMER' | 'ORGANIZER' | string;

export interface ChatMessage {
  id: string;
  threadId: string;
  senderId: string;
  senderRole: SenderRole;
  type: ChatMessageType;
  body?: string | null;
  orderId?: string | null;
  order?: OrderSummary | null;
  createdAt: string;
}

export interface ChatThread {
  id: string;
  branchId: string;
  customerId: string;
  lastMessageAt?: string | null;
  // Convenience fields the backend may include; optional.
  branchName?: string;
}

export interface OrderBranch {
  id: string;
  name: string;
  organizerId?: string;
}

export interface Paged<T> {
  items: T[];
  page: number;
  pageSize?: number;
  total: number;
}

// New item line typed by the customer in the create-order modal (no orderNumber).
export interface NewOrderItemInput {
  name: string;
  quantity: number;
  unit?: string;
}

// Contract error envelope: { error: { code, message, fieldErrors? } }
export interface ApiErrorEnvelope {
  error: {
    code: 'VALIDATION' | 'UNAUTHORIZED' | 'FORBIDDEN' | 'NOT_FOUND' | 'CONFLICT' | string;
    message: string;
    fieldErrors?: Record<string, string>;
  };
}

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  CREATED: 'Draft',
  SUBMITTED_TO_BRANCH: 'Submitted',
  UNDER_REVIEW: 'Under review',
  UPDATED: 'Updated by branch',
  DELIVERED: 'Delivered',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

export const ORDER_ITEM_STATUS_LABELS: Record<OrderItemStatus, string> = {
  REQUESTED: 'Requested',
  AVAILABLE: 'Available',
  UNAVAILABLE: 'Out of stock',
  SUBSTITUTED: 'Substituted',
};
